import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, ListGroup } from 'react-bootstrap';
import axios from 'axios';
import { LanguageContext } from '../contexts/LanguageProvider';
import { AuthContext } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../configs/apiConfig';
import {
  FaBookOpen, FaArrowRight, FaSearch, FaTasks, FaUsers, FaComments,
  FaMapMarkedAlt, FaEdit, FaLeaf, FaRegFileAlt, FaSeedling,
  FaCheckCircle, FaInstagram,
} from 'react-icons/fa';

const translations = {
  pt: {
    eyebrow: 'Plataforma Biotrop',
    heroTitle: 'Conhecimento em bioinsumos para sua cafeicultura',
    heroSubtitleGuest: 'Um livro virtual construído por pesquisadores e revisado continuamente: manejo biológico, produtos e boas práticas para o café.',
    heroNote: 'Acesso gratuito — basta criar sua conta.',
    explore: 'Explorar o livro',
    register: 'Criar conta',
    login: 'Entrar',
    coverKicker: 'Livro virtual',
    coverTitle: 'Bioinsumos na Cafeicultura',
    coverFoot: 'Conteúdo revisado por editores e especialistas.',
    chapterSample1: 'Introdução aos bioinsumos',
    chapterSample2: 'Controle biológico de pragas',
    chapterSample3: 'Microbiologia do solo',
    chapterSample4: 'Boas práticas de aplicação',
    realizacao: 'Realização',
    featuresTitle: 'O que você encontra na plataforma',
    featuresSub: 'Conteúdo técnico organizado em capítulos, com trilha de leitura e canal aberto para contribuição.',
    f1Title: 'Capítulos revisados',
    f1Text: 'Conteúdo escrito e revisado por editores, com histórico de versões e controle de publicação.',
    f2Title: 'Busca por microrganismo ou prática',
    f2Text: 'Pesquise por microrganismo, praga ou prática de manejo e veja em quais capítulos o assunto é tratado.',
    f3Title: 'Sugestões e comentários',
    f3Text: 'Aponte correções e faça sugestões dentro do capítulo. A equipe editorial avalia cada envio.',
    howTitle: 'Como funciona',
    s1Title: 'Crie sua conta',
    s1Text: 'Cadastro rápido, com o perfil da sua atuação — produtor, pesquisador, estudante ou consultor.',
    s2Title: 'Leia no seu ritmo',
    s2Text: 'A plataforma marca os capítulos já lidos e guarda de onde você parou.',
    s3Title: 'Contribua',
    s3Text: 'Comente, sugira ajustes e ajude a melhorar o material para toda a cadeia do café.',
    ctaTitle: 'Pronto para começar?',
    ctaText: 'Crie sua conta gratuita e comece a explorar o livro de bioinsumos na cafeicultura.',
    ctaBtn: 'Criar minha conta',
    footerAbout: 'Plataforma de conhecimento em bioinsumos para a cafeicultura, construída em parceria entre universidade, pesquisa e indústria.',
    footerContact: 'Contato',
    footerBeta: 'Beta',
    footerBetaText: 'Estamos melhorando a plataforma — encontrou algo estranho? Fale com a gente.',
    developedBy: 'Desenvolvido por UFLA, Biotrop e Zetta.',
    greetEyebrow: 'Bem-vindo de volta',
    greetHello: 'Olá',
    greetText: 'Continue a leitura, acompanhe suas contribuições e explore novos capítulos.',
    searchPlaceholder: 'Buscar por tema (ex.: nematoides, Bacillus, café)...',
    searchResults: 'Resultados para',
    noResults: 'Nenhum capítulo encontrado para esse termo.',
    clear: 'Limpar',
    quickActions: 'Atalhos',
    availableBooks: 'Livros disponíveis',
    availableBooksSub: 'Escolha um livro para ver o sumário e começar a leitura.',
    chapters: 'capítulos',
    openBook: 'Abrir livro',
    continueReading: 'Continue de onde parou',
    continueBtn: 'Continuar',
    noBooks: 'Nenhum livro disponível no momento.',
    viewBooks: 'Visualizar Livros',
    myComments: 'Meus Comentários',
    editBooks: 'Editar Livros',
    tasks: 'Tarefas',
    comments: 'Comentários',
    users: 'Usuários',
  },
  en: {
    eyebrow: 'Biotrop Platform',
    heroTitle: 'Bioinputs knowledge for your coffee farm',
    heroSubtitleGuest: 'A virtual book written by researchers and continuously reviewed: biological management, products and best practices for coffee.',
    heroNote: 'Free access — just create your account.',
    explore: 'Explore the book',
    register: 'Create account',
    login: 'Sign in',
    coverKicker: 'Virtual book',
    coverTitle: 'Bioinputs in Coffee Growing',
    coverFoot: 'Content reviewed by editors and specialists.',
    chapterSample1: 'Introduction to bioinputs',
    chapterSample2: 'Biological pest control',
    chapterSample3: 'Soil microbiology',
    chapterSample4: 'Application best practices',
    realizacao: 'Partners',
    featuresTitle: 'What you find on the platform',
    featuresSub: 'Technical content organized in chapters, with a reading trail and an open channel for contribution.',
    f1Title: 'Reviewed chapters',
    f1Text: 'Content written and reviewed by editors, with version history and publication control.',
    f2Title: 'Search by microorganism or practice',
    f2Text: 'Search by microorganism, pest or management practice and see which chapters cover the subject.',
    f3Title: 'Suggestions and comments',
    f3Text: 'Point out corrections and make suggestions inside the chapter. The editorial team reviews every submission.',
    howTitle: 'How it works',
    s1Title: 'Create your account',
    s1Text: 'Quick sign-up with your profile — producer, researcher, student or consultant.',
    s2Title: 'Read at your pace',
    s2Text: 'The platform marks the chapters you have read and remembers where you stopped.',
    s3Title: 'Contribute',
    s3Text: 'Comment, suggest changes and help improve the material for the whole coffee chain.',
    ctaTitle: 'Ready to start?',
    ctaText: 'Create your free account and start exploring the book of bioinputs in coffee growing.',
    ctaBtn: 'Create my account',
    footerAbout: 'A knowledge platform on bioinputs for coffee growing, built in partnership between university, research and industry.',
    footerContact: 'Contact',
    footerBeta: 'Beta',
    footerBetaText: 'We are improving the platform — found something odd? Let us know.',
    developedBy: 'Developed by UFLA, Biotrop and Zetta.',
    greetEyebrow: 'Welcome back',
    greetHello: 'Hello',
    greetText: 'Continue reading, track your contributions and explore new chapters.',
    searchPlaceholder: 'Search by topic (e.g., nematodes, Bacillus, coffee)...',
    searchResults: 'Results for',
    noResults: 'No chapter found for this term.',
    clear: 'Clear',
    quickActions: 'Shortcuts',
    availableBooks: 'Available books',
    availableBooksSub: 'Pick a book to see its table of contents and start reading.',
    chapters: 'chapters',
    openBook: 'Open book',
    continueReading: 'Continue where you left off',
    continueBtn: 'Continue',
    noBooks: 'No books available right now.',
    viewBooks: 'View Books',
    myComments: 'My Comments',
    editBooks: 'Edit Books',
    tasks: 'Tasks',
    comments: 'Comments',
    users: 'Users',
  },
};

const PARTNERS = ['UFLA', 'Biotrop', 'Zetta'];

const CardSkeleton = () => (
  <div className="col-12 col-md-6 col-lg-4">
    <Card className="h-100">
      <Card.Body className="d-flex align-items-center gap-3">
        <div className="skeleton" style={{ width: 42, height: 42, borderRadius: 12 }} />
        <div className="flex-grow-1">
          <div className="skeleton mb-2" style={{ height: 14, width: '70%' }} />
          <div className="skeleton" style={{ height: 10, width: '40%' }} />
        </div>
      </Card.Body>
    </Card>
  </div>
);

const SiteFooter = ({ t }) => (
  <footer className="site-footer">
    <div className="site-footer-grid">
      <div>
        <div className="site-footer-brand">
          <img src={process.env.PUBLIC_URL + '/biotrop-icon.png'} alt="Biotrop" className="site-footer-mark" />
          <span>
            <span className="site-footer-name d-block">BIOTROP</span>
            <span className="site-footer-tagline">Soluções em Tecnologia Biológica</span>
          </span>
        </div>
        <p className="site-footer-col mt-3 mb-0">{t.footerAbout}</p>
      </div>

      <div className="site-footer-col">
        <strong>{t.realizacao}</strong>
        {t.developedBy}
      </div>

      <div className="site-footer-col">
        <strong>{t.footerContact}</strong>
        <a
          className="d-inline-flex align-items-center gap-2"
          href="https://www.instagram.com/biotropbr/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaInstagram /> @biotropbr
        </a>
      </div>
    </div>

    <div className="site-footer-bottom">
      <span className="d-inline-flex align-items-center gap-2">
        <span className="beta-pill">{t.footerBeta}</span>
        {t.footerBetaText}
      </span>
      <span>{PARTNERS.join(' · ')}</span>
    </div>
  </footer>
);

const GuestLanding = ({ t, onLogin }) => {
  const features = [
    { icon: <FaCheckCircle />, title: t.f1Title, text: t.f1Text },
    { icon: <FaSearch />, title: t.f2Title, text: t.f2Text },
    { icon: <FaComments />, title: t.f3Title, text: t.f3Text },
  ];
  const steps = [
    { title: t.s1Title, text: t.s1Text },
    { title: t.s2Title, text: t.s2Text },
    { title: t.s3Title, text: t.s3Text },
  ];
  const chapters = [
    { label: t.chapterSample1, read: true },
    { label: t.chapterSample2, read: true },
    { label: t.chapterSample3, read: false },
    { label: t.chapterSample4, read: false },
  ];

  return (
    <>
      <section className="home-hero">
        <div className="hero-grid">
          <div className="hero-copy">
            <span className="home-hero-eyebrow"><FaLeaf /> {t.eyebrow}</span>
            <h1 className="home-hero-title">{t.heroTitle}</h1>
            <p className="home-hero-subtitle">{t.heroSubtitleGuest}</p>
            <div className="hero-actions">
              <Button size="lg" onClick={onLogin}>
                <FaBookOpen className="me-2" /> {t.explore}
              </Button>
              <Button as={Link} to="/register" variant="outline-light" size="lg" className="fw-bold border-2">
                {t.register}
              </Button>
            </div>
            <div className="hero-note"><FaSeedling /> {t.heroNote}</div>
          </div>

          <div className="hero-visual" aria-hidden="true">
            <div className="hero-book">
              <img src={process.env.PUBLIC_URL + '/biotrop-icon-white.png'} alt="" className="hero-book-mark" />
              <span className="hero-book-kicker">{t.coverKicker}</span>
              <h2 className="hero-book-title">{t.coverTitle}</h2>
              {chapters.map((c, i) => (
                <div key={c.label} className={`hero-book-line${c.read ? ' hero-book-line--read' : ''}`}>
                  <span className="hero-book-num">{i + 1}</span>
                  <span className="text-truncate">{c.label}</span>
                </div>
              ))}
              <div className="hero-book-foot">{t.coverFoot}</div>
            </div>
          </div>
        </div>
      </section>

      <div className="credibility-strip">
        <span className="credibility-label">{t.realizacao}</span>
        {PARTNERS.map((p) => <span className="credibility-item" key={p}>{p}</span>)}
      </div>

      <section className="landing-section">
        <div className="section-head">
          <h2 className="section-head-title">{t.featuresTitle}</h2>
          <p className="section-head-sub">{t.featuresSub}</p>
        </div>
        <div className="row g-3">
          {features.map((f) => (
            <div className="col-12 col-md-4" key={f.title}>
              <div className="feature-card">
                <span className="feature-icon">{f.icon}</span>
                <div className="feature-title">{f.title}</div>
                <p className="feature-text">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-section">
        <div className="section-head">
          <h2 className="section-head-title">{t.howTitle}</h2>
        </div>
        <div className="step-row">
          {steps.map((s, i) => (
            <div className="step-card" key={s.title}>
              <span className="step-num">{i + 1}</span>
              <div className="step-title">{s.title}</div>
              <p className="step-text">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-section">
        <div className="cta-band">
          <div>
            <h2 className="cta-band-title">{t.ctaTitle}</h2>
            <p className="cta-band-text">{t.ctaText}</p>
          </div>
          <Button as={Link} to="/register" size="lg">{t.ctaBtn}</Button>
        </div>
      </section>
    </>
  );
};

const HomePage = () => {
  const { language } = useContext(LanguageContext);
  const { isLoggedIn, role, username, name, setLoginModalOpen } = useContext(AuthContext);
  const navigate = useNavigate();
  const t = translations[language] || translations.pt;

  const basePath = role ? `/${role.toLowerCase()}` : '';
  const authHeader = () => {
    const auth = localStorage.getItem('auth');
    return auth ? { Authorization: `Basic ${auth}` } : {};
  };

  const [books, setBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [lastRead, setLastRead] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [stat, setStat] = useState(null);

  useEffect(() => {
    if (!isLoggedIn) return;
    let active = true;
    setLoadingBooks(true);
    axios.get(API_ENDPOINTS.BOOKS_REFERENCES, { headers: authHeader() })
      .then((res) => {
        if (!active) return;
        setBooks(Array.isArray(res.data) ? res.data.filter((b) => b.visible !== false) : []);
      })
      .catch(() => active && setBooks([]))
      .finally(() => active && setLoadingBooks(false));
    return () => { active = false; };
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !username) { setLastRead(null); return; }
    try {
      const raw = localStorage.getItem(`lastRead_${username}`);
      setLastRead(raw ? JSON.parse(raw) : null);
    } catch (e) {
      setLastRead(null);
    }
  }, [isLoggedIn, username]);

  useEffect(() => {
    if (!isLoggedIn) { setStat(null); return; }
    let url = null;
    if (role === 'EDITOR') url = API_ENDPOINTS.TASKS_ASSIGNED;
    else if (role === 'ADMIN') url = API_ENDPOINTS.USERS;
    if (!url) { setStat(null); return; }
    let active = true;
    axios.get(url, { headers: authHeader() })
      .then((res) => active && setStat(Array.isArray(res.data) ? res.data.length : null))
      .catch(() => active && setStat(null));
    return () => { active = false; };
  }, [isLoggedIn, role]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) { setResults(null); return; }
    setSearching(true);
    try {
      const res = await axios.get(API_ENDPOINTS.CHAPTERS_SEARCH(q), { headers: authHeader() });
      setResults(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const quickActions = ({
    READER: [
      { to: `${basePath}/read`, label: t.viewBooks, icon: <FaBookOpen /> },
      { to: `${basePath}/comment`, label: t.myComments, icon: <FaComments /> },
    ],
    EDITOR: [
      { to: `${basePath}/edit-books`, label: t.editBooks, icon: <FaEdit /> },
      { to: `${basePath}/tasks`, label: t.tasks, icon: <FaTasks />, count: stat },
      { to: `${basePath}/comment`, label: t.comments, icon: <FaComments /> },
    ],
    ADMIN: [
      { to: `${basePath}/roles`, label: t.users, icon: <FaUsers />, count: stat },
      { to: `${basePath}/comment`, label: t.comments, icon: <FaComments /> },
      { to: `${basePath}/geo`, label: 'Geo', icon: <FaMapMarkedAlt /> },
    ],
  })[role] || [];

  const firstName = String(name || username || '').trim().split(/\s+/)[0];

  if (!isLoggedIn) {
    return (
      <div className="home-page">
        <GuestLanding t={t} onLogin={() => setLoginModalOpen(true)} />
        <SiteFooter t={t} />
      </div>
    );
  }

  return (
    <div className="home-page">
      <section className="greet-hero">
        <div>
          <span className="greet-hero-eyebrow">{t.greetEyebrow}</span>
          <h1 className="greet-hero-title">
            {t.greetHello}{firstName ? `, ${firstName}` : ''}!
          </h1>
          <p className="greet-hero-text">{t.greetText}</p>
        </div>
        <div className="greet-hero-actions">
          <Button size="lg" onClick={() => navigate(`${basePath}/read`)}>
            <FaBookOpen className="me-2" /> {t.explore}
          </Button>
        </div>
        <form className="home-search w-100" onSubmit={handleSearch}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.searchPlaceholder}
            aria-label={t.searchPlaceholder}
          />
          <button type="submit" aria-label={t.searchPlaceholder}><FaSearch /></button>
        </form>
      </section>

      {results !== null && (
        <section className="landing-section">
          <div className="section-head">
            <h2 className="section-head-title">
              {t.searchResults} <span className="text-primary">"{query}"</span>
            </h2>
            <Button variant="link" size="sm" onClick={() => { setResults(null); setQuery(''); }}>
              {t.clear}
            </Button>
          </div>
          {searching ? (
            <ListGroup>
              {[0, 1, 2].map((i) => (
                <ListGroup.Item key={i}>
                  <div className="skeleton" style={{ height: 14, width: '55%' }} />
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : results.length > 0 ? (
            <ListGroup>
              {results.map((c) => (
                <ListGroup.Item
                  action
                  key={c.id}
                  onClick={() => navigate(`${basePath}/read/chapter/${c.id}`)}
                  className="d-flex justify-content-between align-items-center"
                >
                  <span className="d-inline-flex align-items-center gap-2">
                    <FaRegFileAlt className="text-primary" />
                    {c.title}
                  </span>
                  <FaArrowRight className="text-muted" />
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon"><FaSearch /></div>
              {t.noResults}
            </div>
          )}
        </section>
      )}

      {lastRead && lastRead.chapterId && (
        <section className="mb-4">
          <Card
            className="continue-card"
            role="button"
            onClick={() => navigate(`${basePath}/read/chapter/${lastRead.chapterId}`)}
          >
            <Card.Body className="d-flex align-items-center gap-3">
              <span className="book-card-icon"><FaBookOpen /></span>
              <div className="flex-grow-1">
                <span className="section-label d-block mb-1">{t.continueReading}</span>
                <div className="fw-bold">{lastRead.chapterTitle}</div>
                {lastRead.bookTitle && <small className="text-muted">{lastRead.bookTitle}</small>}
              </div>
              <Button size="sm">{t.continueBtn}</Button>
            </Card.Body>
          </Card>
        </section>
      )}

      {quickActions.length > 0 && (
        <section className="landing-section">
          <div className="section-head">
            <h2 className="section-head-title">{t.quickActions}</h2>
          </div>
          <div className="row g-3">
            {quickActions.map((a) => (
              <div className="col-6 col-md-4 col-lg-3" key={a.to}>
                <Card className="h-100 quick-card" role="button" onClick={() => navigate(a.to)}>
                  <Card.Body className="d-flex align-items-center gap-3">
                    {typeof a.count === 'number' ? (
                      <div className="quick-count">{a.count}</div>
                    ) : (
                      <div className="quick-icon">{a.icon}</div>
                    )}
                    <div className="small fw-bold">{a.label}</div>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="landing-section">
        <div className="section-head">
          <h2 className="section-head-title">{t.availableBooks}</h2>
          <p className="section-head-sub">{t.availableBooksSub}</p>
        </div>
        {loadingBooks ? (
          <div className="row g-3">
            {[0, 1, 2].map((i) => <CardSkeleton key={i} />)}
          </div>
        ) : books.length > 0 ? (
          <div className="row g-3">
            {books.map((book) => (
              <div className="col-12 col-md-6 col-lg-4" key={book.id}>
                <Card
                  className="h-100 quick-card"
                  role="button"
                  onClick={() => navigate(`${basePath}/read/book/${book.id}`)}
                >
                  <Card.Body className="d-flex align-items-center gap-3">
                    <span className="book-card-icon"><FaBookOpen /></span>
                    <div className="flex-grow-1">
                      <div className="fw-bold">{book.title}</div>
                      <small className="text-muted">
                        {Array.isArray(book.chapters) && book.chapters.length > 0
                          ? `${book.chapters.length} ${t.chapters}`
                          : t.openBook}
                      </small>
                    </div>
                    <FaArrowRight className="text-muted" />
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon"><FaBookOpen /></div>
            {t.noBooks}
          </div>
        )}
      </section>

      <SiteFooter t={t} />
    </div>
  );
};

export default HomePage;
